import { useEffect, useRef } from 'react';
import { CheckCircleIcon, SpinnerGapIcon, WarningCircleIcon, XIcon } from '@phosphor-icons/react';
import { useToast } from '@/hooks/use-toast';
import { isListMembershipJobLive, useListMembershipJob } from '@/modules/lists/hooks/use-lists';
import type { ListMembershipJobApi } from '@/modules/lists/types/lists-api.types';

type ListMembershipJobProgressProps = {
  jobId: string;
  /** List names by id, for the summary — a list missing here is shown by its id. */
  listNamesById: Record<string, string>;
  onDismiss: () => void;
};

const formatCount = (value: number) => value.toLocaleString();

/** "Bern: 320, Basel: 12" — what actually changed per list. */
export const summarizeListMembershipJob = (
  job: Pick<ListMembershipJobApi, 'results'>,
  listNamesById: Record<string, string>
): string =>
  job.results
    .map((result) => `${listNamesById[result.listId] ?? result.listId}: ${formatCount(result.changed)}`)
    .join(', ');

/**
 * Follows a "select all" add-to-lists job: progress while it runs, one toast when it ends. A job can
 * be a million contacts, so the page stays usable meanwhile — this is a strip, not a modal.
 */
export const ListMembershipJobProgress = ({ jobId, listNamesById, onDismiss }: ListMembershipJobProgressProps) => {
  const { toast } = useToast();
  const { data: job, isError, error } = useListMembershipJob(jobId);
  const announcedRef = useRef(false);

  useEffect(() => {
    if (!job || isListMembershipJobLive(job) || announcedRef.current) return;
    announcedRef.current = true;

    if (job.status === 'completed') {
      toast({
        variant: 'success',
        title: 'Added to lists',
        description: `Added ${summarizeListMembershipJob(job, listNamesById)}. Contacts already in a list were left as they were.`,
      });
      return;
    }

    toast({
      variant: 'destructive',
      title: 'Adding to lists failed',
      description: job.lastError || 'The job could not be completed.',
    });
  }, [job, listNamesById, toast]);

  const live = !job || isListMembershipJobLive(job);
  const failed = isError || job?.status === 'failed';

  return (
    <div
      role="status"
      aria-live="polite"
      className={`flex items-center gap-3 rounded-lg border px-4 py-2.5 text-sm ${
        failed
          ? 'border-destructive/30 bg-destructive/5 text-destructive'
          : 'border-primary-200 bg-primary-50 text-primary-700'
      }`}
    >
      {live && !isError ? (
        <SpinnerGapIcon className="size-4 shrink-0 animate-spin" />
      ) : failed ? (
        <WarningCircleIcon className="size-4 shrink-0" />
      ) : (
        <CheckCircleIcon className="size-4 shrink-0" />
      )}

      <span className="flex-1">
        {isError
          ? error instanceof Error
            ? error.message
            : 'Could not read the job status.'
          : !job || job.status === 'queued'
            ? 'Adding contacts to lists — waiting to start…'
            : job.status === 'processing'
              ? `Adding contacts to lists… ${formatCount(job.processed)} processed`
              : job.status === 'completed'
                ? `Done — added ${summarizeListMembershipJob(job, listNamesById)}.`
                : `Failed — ${job.lastError || 'the job could not be completed.'}`}
      </span>

      {!live || isError ? (
        <button
          type="button"
          className="text-neutral-600 hover:text-neutral-900"
          onClick={onDismiss}
          aria-label="Dismiss"
        >
          <XIcon className="size-4" />
        </button>
      ) : null}
    </div>
  );
};
